// Чтение `location.state.from` без `as`-кастов.
//
// LoginPage/RegisterPage/SelectOrgPage кладут в `state.from` путь, куда
// нужно вернуться после того, как активная организация обеспечена
// (ADR-0038, фаза 2). `location.state` типизирован как `unknown` —
// сужаем его вручную, а не кастом.
//
// `from` — недоверенный вход: `<ProtectedRoute>` кладёт туда СВОЙ
// `location.pathname` (см. `ProtectedRoute.tsx`), т.е. значение
// приходит от текущего URL, а не только от заранее известных мест
// вызова. Поэтому `readRedirectTarget` — единственная точка сужения,
// закрывающая сразу два дефекта:
//
// - open redirect: значение обязано быть внутренним путём приложения —
//   начинается РОВНО с одного `/`, а второй символ не `/` и не `\`.
//   Оба символа браузерный URL-парсер трактует как разделитель
//   authority для URL со специальной схемой (http/https): `//evil.com`
//   — protocol-relative (наследует текущую схему), а `/\evil.com`
//   парсер нормализует так же, потому что для специальных схем `\`
//   равнозначен `/`.
// - петля `/select-org`: сам роут (`SELECT_ORG_PATH`, тот же путь, что
//   и в `App.tsx`) обёрнут в `<ProtectedRoute>`. Аноним, открывший
//   `/select-org` напрямую, получает `state.from === "/select-org"` от
//   самого ProtectedRoute → после логина и авто-switch страница
//   попыталась бы `navigate("/select-org")` на саму себя. Компонент не
//   размонтируется, `canSwitch` уже `false` (мутация не idle), эффект
//   молчит — пользователь застревает на экране выбора организации.
//   Поэтому путь `/select-org` как цель редиректа отвергается наравне
//   с внешними URL; сравнение — по pathname (части до `?`/`#`), не по
//   всей строке, иначе `/select-org?x=1` прошёл бы мимо проверки.
//
// Инвариант результата: всегда начинается с одного `/`, никогда не
// protocol-relative (ни `//…`, ни `/\…`) и путь никогда не равен
// `/select-org`.

// Развилка, ведущая в первый доступный раздел по составу модулей
// (ADR-0083): модульного раздела, гарантированно доступного после входа,
// нет, поэтому ссылки «в начало» ведут сюда, а не на конкретный раздел.
export const HOME_PATH = "/home";

export const DEFAULT_REDIRECT = HOME_PATH;

export const SELECT_ORG_PATH = "/select-org";

function pathnameOf(value: string): string {
  const queryOrHashIndex = value.search(/[?#]/);
  return queryOrHashIndex === -1 ? value : value.slice(0, queryOrHashIndex);
}

function isSafeInternalRedirect(value: string): boolean {
  if (!value.startsWith("/")) {
    return false;
  }
  const secondChar = value.charAt(1);
  if (secondChar === "/" || secondChar === "\\") {
    return false;
  }
  return pathnameOf(value) !== SELECT_ORG_PATH;
}

export function readRedirectTarget(state: unknown): string {
  if (typeof state !== "object" || state === null || !("from" in state)) {
    return DEFAULT_REDIRECT;
  }
  const { from } = state;
  if (typeof from !== "string" || !isSafeInternalRedirect(from)) {
    return DEFAULT_REDIRECT;
  }
  return from;
}
